/**
 * Everybody else on the board: where their pointer is and what they are
 * drawing.
 *
 * None of it is stored by the server, and none of it is ever taken back by a
 * frame: a socket that drops without closing never says it left, and the board
 * has no alarm to notice. So a party that has sent nothing for `PEER_TTL_MS` is
 * dropped here, cursor and live stroke both, and comes back with its next
 * frame; a live stroke with no `live` frame for that long goes on its own. A
 * live element also goes the moment an `add` lands under its id — that op is
 * the finished element replacing it — and a party goes when `presence` stops
 * listing it.
 */

import { participantLabel } from '../relay/types';
import type { BoardElement, BoardParty, RemoteOp } from './types';

/** Silence after which a cursor is taken down. */
export const PEER_TTL_MS = 5_000;

/**
 * Eight hues off the brand ramps, distinct from each other and legible on both
 * grounds: violet 600, mauve 500, teal 500, warning 500, danger 500, indigo 400,
 * teal 700, mauve 400. The order is the contract's — the desktop indexes the
 * same list with the same hash, so a person is one colour on every screen.
 */
export const PARTY_COLOURS = [
  '#796cbf', '#886193', '#0fb3a8', '#e8a33d', '#d94f6f', '#6f78bd', '#0f736d', '#ac89b6'
] as const;

/** FNV-1a, 32-bit, over the id's UTF-8 bytes, modulo the palette. */
export function partyColour(id: string): string {
  let hash = 0x811c9dc5;
  for (const byte of new TextEncoder().encode(id)) {
    hash ^= byte;
    hash = Math.imul(hash, 0x01000193) >>> 0;
  }
  return PARTY_COLOURS[hash % PARTY_COLOURS.length]!;
}

/** A cursor as the canvas draws it. */
export interface PeerCursor {
  id: string;
  /** Through `participantLabel`; empty until `presence` has named the party. */
  label: string;
  colour: string;
  x: number;
  y: number;
}

/** Somebody with the board open, for the roster. */
export interface PeerHere {
  id: string;
  label: string;
  colour: string;
}

export interface PeerView {
  cursors: PeerCursor[];
  live: BoardElement[];
  /** Everybody else the server says has the board open, in arrival order. */
  here: PeerHere[];
}

interface Peer {
  seen: number;
  cursor?: { x: number; y: number };
  live?: BoardElement;
  /** When `live` last changed. Timed apart from `seen`, because a gesture
   *  whose end was lost would otherwise stay up for as long as its author's
   *  cursor kept moving. */
  liveSeen?: number;
}

export class BoardPeers {
  private names = new Map<string, BoardParty>();
  private peers = new Map<string, Peer>();
  private you = '';
  /** The last `view().live`, handed back while nothing in it changed so the
   *  canvas does not repaint every element for every cursor frame. */
  private shown: BoardElement[] = [];

  /** Also drops whoever the roster no longer lists: their socket closed. */
  presence(parties: BoardParty[], you: string): void {
    this.you = you;
    this.names = new Map(parties.map((p) => [p.id, p]));
    for (const id of this.peers.keys()) {
      if (!this.names.has(id)) this.peers.delete(id);
    }
  }

  cursor(by: string, x: number, y: number, now: number): void {
    if (by === this.you) return;
    const peer = this.touch(by, now);
    peer.cursor = { x, y };
  }

  live(by: string, el: BoardElement | null, now: number): void {
    if (by === this.you) return;
    const peer = this.touch(by, now);
    if (el) {
      peer.live = el;
      peer.liveSeen = now;
    } else {
      delete peer.live;
    }
  }

  /** An `add` is the finished element, so it replaces the live one. */
  op(remote: RemoteOp): void {
    if (remote.op.k !== 'add') return;
    const id = remote.op.el.id;
    for (const peer of this.peers.values()) {
      if (peer.live?.id === id) delete peer.live;
    }
  }

  /** Drops everybody silent for `PEER_TTL_MS`. True when anybody went. */
  expire(now: number): boolean {
    let changed = false;
    for (const [id, peer] of this.peers) {
      if (now - peer.seen >= PEER_TTL_MS) {
        this.peers.delete(id);
        changed = true;
      } else if (peer.live && now - (peer.liveSeen ?? 0) >= PEER_TTL_MS) {
        delete peer.live;
        changed = true;
      }
    }
    return changed;
  }

  view(): PeerView {
    const cursors: PeerCursor[] = [];
    const live: BoardElement[] = [];
    for (const [id, peer] of this.peers) {
      if (peer.live) live.push(peer.live);
      if (!peer.cursor) continue;
      const party = this.names.get(id);
      cursors.push({
        id,
        label: party ? participantLabel({ displayName: party.name, guest: party.guest }) : '',
        colour: partyColour(id),
        x: peer.cursor.x,
        y: peer.cursor.y
      });
    }
    const same = live.length === this.shown.length && live.every((el, i) => el === this.shown[i]);
    if (!same) this.shown = live;
    const here: PeerHere[] = [];
    for (const [id, party] of this.names) {
      if (id === this.you) continue;
      here.push({ id, label: participantLabel({ displayName: party.name, guest: party.guest }), colour: partyColour(id) });
    }
    return { cursors, live: this.shown, here };
  }

  private touch(by: string, now: number): Peer {
    let peer = this.peers.get(by);
    if (!peer) {
      peer = { seen: now };
      this.peers.set(by, peer);
    }
    peer.seen = now;
    return peer;
  }
}
